import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function Lightbox({ images, currentIndex, onClose, onPrev, onNext, projectTitle }) {
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (images && images.length > 1) {
        if (e.key === 'ArrowLeft') onPrev();
        if (e.key === 'ArrowRight') onNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, images]);

  if (currentIndex === null || !images || images.length === 0) return null;

  // Touch swipe handling
  const handleTouchStart = (e) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    const minSwipeDistance = 50; // minimum distance in pixels to trigger swipe

    if (distance > minSwipeDistance) {
      // Swiped left -> next image
      onNext();
    } else if (distance < -minSwipeDistance) {
      // Swiped right -> previous image
      onPrev();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-12 select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white hover:text-slate-300 transition-colors z-[120] bg-black/40 hover:bg-black/60 p-2 rounded-full backdrop-blur-xs"
        aria-label="Luk galleri"
      >
        <X size={28} className="md:w-8 md:h-8" />
      </button>

      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent touch events from triggering on container
            onPrev();
          }}
          className="absolute left-4 md:left-6 text-white hover:text-slate-300 transition-colors z-[120] bg-black/40 hover:bg-black/60 p-3 rounded-full backdrop-blur-xs"
          aria-label="Forrige billede"
        >
          <ChevronLeft size={28} className="md:w-10 md:h-10" />
        </button>
      )}

      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent touch events from triggering on container
            onNext();
          }}
          className="absolute right-4 md:right-6 text-white hover:text-slate-300 transition-colors z-[120] bg-black/40 hover:bg-black/60 p-3 rounded-full backdrop-blur-xs"
          aria-label="Næste billede"
        >
          <ChevronRight size={28} className="md:w-10 md:h-10" />
        </button>
      )}

      <div className="relative w-full h-full flex flex-col items-center justify-center pointer-events-none">
        <figure className="relative max-w-full max-h-[85vh] flex flex-col items-center justify-center m-0">
          <img
            src={images[currentIndex]}
            alt={projectTitle ? `Projektfoto ${currentIndex + 1} af ${images.length} – ${projectTitle} | CM Skruefundament` : `Skruefundament referencebillede ${currentIndex + 1} af ${images.length}`}
            className="max-w-full max-h-[75vh] md:max-h-[80vh] object-contain shadow-2xl pointer-events-auto rounded-lg"
          />
          <figcaption className="mt-3 text-center text-white/90 text-sm font-medium tracking-wide bg-slate-900/80 px-5 py-2 rounded-full backdrop-blur-xs pointer-events-auto flex items-center gap-3">
            <span>{projectTitle ? `${projectTitle}` : 'Skruefundament reference'}</span>
            {images.length > 1 && (
              <span className="text-amber-400 font-bold border-l border-slate-700 pl-3">
                {currentIndex + 1} / {images.length}
              </span>
            )}
          </figcaption>
        </figure>
      </div>
    </div>
  );
}
